/**
 * 财务尽职调查组件
 * 步骤3.2: 财务尽职调查
 * 基于 PROFESSIONAL_DUE_DILIGENCE_DESIGN.md 设计规范
 */

import React from 'react';
import {
  Form,
  Row,
  Col,
  Input,
  Select,
  Radio,
  DatePicker,
  InputNumber,
  Button,
} from 'antd';
import { UnifiedDataTable } from '../../../../components/unified/UnifiedDataTable';
import {
  DollarOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
  SafetyCertificateOutlined,
  AlertOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  FormSection,
  FormSubSection,
  ConditionalField,
  WhenTrue,
  WhenFalse,
  RiskMatrix,
  DocumentUpload,
  useDDAutoSave,
  useDDValidation,
  DDValidationRules,
  AutoSaveIndicator,
} from './shared';
import type {
  FinancialDueDiligenceProps,
  FinancialDueDiligenceData,
  AccountingPolicyChange,
  DataVerification,
  RelatedParty,
  RelatedTransaction,
  Guarantee,
  LitigationImpact,
  ContingentLiability,
  InternalControlSystem,
  InternalControlRecommendation,
  FinancialRisk,
  RiskLevel,
} from './shared/types';

const { Option } = Select;
const { TextArea } = Input;

const FinancialDueDiligence: React.FC<FinancialDueDiligenceProps> = ({
  projectId,
  onStepComplete,
  onStepSave,
  readonly = false,
}) => {
  const [form] = Form.useForm<FinancialDueDiligenceData>();

  // 使用专业尽调专用Hooks
  const { validateForm } = useDDValidation({
    form,
    stepType: 'financial',
  });

  const { saving, saveStatus, lastSavedText, triggerAutoSave, manualSave } =
    useDDAutoSave({
      projectId,
      stepType: 'financial',
      enabled: true,
      onSave: async data => {
        console.log('Financial DD auto saved:', data);
      },
    });

  // 监听表单变化
  const handleFormChange = () => {
    const currentValues = form.getFieldsValue();
    triggerAutoSave(currentValues);
  };

  // 保存草稿 - 通过统一操作栏触发
  const handleSaveDraft = async () => {
    const values = form.getFieldsValue();
    await manualSave(values);
    onStepSave?.('financial_due_diligence', values);
  };

  // 提交表单 - 通过统一操作栏触发
  const handleSubmit = async () => {
    const result = await validateForm();
    if (result.success) {
      onStepComplete?.('financial_due_diligence', result.values);
    }
  };

  // 会计政策变更表格列定义
  const accountingPolicyColumns = [
    {
      title: '变更日期',
      dataIndex: 'changeDate',
      key: 'changeDate',
      width: 120,
      render: (text: string, record: AccountingPolicyChange, index: number) => (
        <DatePicker
          value={text ? dayjs(text) : null}
          onChange={date => {
            const changes =
              form.getFieldValue([
                'financialStatements',
                'accountingPolicyChanges',
              ]) || [];
            const newChanges = [...changes];
            newChanges[index] = {
              ...newChanges[index],
              changeDate: date ? date.format('YYYY-MM-DD') : '',
            };
            form.setFieldValue(
              ['financialStatements', 'accountingPolicyChanges'],
              newChanges
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="变更日期"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '政策领域',
      dataIndex: 'policyArea',
      key: 'policyArea',
      width: 150,
      render: (text: string, record: AccountingPolicyChange, index: number) => (
        <Select
          value={text}
          onChange={value => {
            const changes =
              form.getFieldValue([
                'financialStatements',
                'accountingPolicyChanges',
              ]) || [];
            const newChanges = [...changes];
            newChanges[index] = { ...newChanges[index], policyArea: value };
            form.setFieldValue(
              ['financialStatements', 'accountingPolicyChanges'],
              newChanges
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="选择领域"
          style={{ width: '100%' }}
        >
          <Option value="revenue_recognition">收入确认</Option>
          <Option value="asset_valuation">资产计价</Option>
          <Option value="depreciation">折旧政策</Option>
          <Option value="inventory">存货计价</Option>
          <Option value="provision">准备金计提</Option>
          <Option value="financial_instruments">金融工具</Option>
          <Option value="consolidation">合并报表</Option>
          <Option value="other">其他</Option>
        </Select>
      ),
    },
    {
      title: '原政策',
      dataIndex: 'oldPolicy',
      key: 'oldPolicy',
      width: 200,
      render: (text: string, record: AccountingPolicyChange, index: number) => (
        <TextArea
          value={text}
          onChange={e => {
            const changes =
              form.getFieldValue([
                'financialStatements',
                'accountingPolicyChanges',
              ]) || [];
            const newChanges = [...changes];
            newChanges[index] = {
              ...newChanges[index],
              oldPolicy: e.target.value,
            };
            form.setFieldValue(
              ['financialStatements', 'accountingPolicyChanges'],
              newChanges
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="原会计政策"
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      ),
    },
    {
      title: '新政策',
      dataIndex: 'newPolicy',
      key: 'newPolicy',
      width: 200,
      render: (text: string, record: AccountingPolicyChange, index: number) => (
        <TextArea
          value={text}
          onChange={e => {
            const changes =
              form.getFieldValue([
                'financialStatements',
                'accountingPolicyChanges',
              ]) || [];
            const newChanges = [...changes];
            newChanges[index] = {
              ...newChanges[index],
              newPolicy: e.target.value,
            };
            form.setFieldValue(
              ['financialStatements', 'accountingPolicyChanges'],
              newChanges
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="新会计政策"
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      ),
    },
    {
      title: '影响分析',
      dataIndex: 'impact',
      key: 'impact',
      width: 200,
      render: (text: string, record: AccountingPolicyChange, index: number) => (
        <TextArea
          value={text}
          onChange={e => {
            const changes =
              form.getFieldValue([
                'financialStatements',
                'accountingPolicyChanges',
              ]) || [];
            const newChanges = [...changes];
            newChanges[index] = {
              ...newChanges[index],
              impact: e.target.value,
            };
            form.setFieldValue(
              ['financialStatements', 'accountingPolicyChanges'],
              newChanges
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="对财务报表的影响"
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      ),
    },
    {
      title: '变更原因',
      dataIndex: 'reason',
      key: 'reason',
      width: 200,
      render: (text: string, record: AccountingPolicyChange, index: number) => (
        <TextArea
          value={text}
          onChange={e => {
            const changes =
              form.getFieldValue([
                'financialStatements',
                'accountingPolicyChanges',
              ]) || [];
            const newChanges = [...changes];
            newChanges[index] = {
              ...newChanges[index],
              reason: e.target.value,
            };
            form.setFieldValue(
              ['financialStatements', 'accountingPolicyChanges'],
              newChanges
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="政策变更原因"
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (text: any, record: AccountingPolicyChange, index: number) => (
        <Button
          type="link"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => {
            const changes =
              form.getFieldValue([
                'financialStatements',
                'accountingPolicyChanges',
              ]) || [];
            const newChanges = [...changes];
            newChanges.splice(index, 1);
            form.setFieldValue(
              ['financialStatements', 'accountingPolicyChanges'],
              newChanges
            );
            handleFormChange();
          }}
          disabled={readonly}
        >
          删除
        </Button>
      ),
    },
  ];

  // 关联方表格列定义
  const relatedPartyColumns = [
    {
      title: '关联方名称',
      dataIndex: 'partyName',
      key: 'partyName',
      width: 200,
      render: (text: string, record: RelatedParty, index: number) => (
        <Input
          value={text}
          onChange={e => {
            const parties =
              form.getFieldValue([
                'relatedPartyTransactions',
                'relatedParties',
              ]) || [];
            const newParties = [...parties];
            newParties[index] = {
              ...newParties[index],
              partyName: e.target.value,
            };
            form.setFieldValue(
              ['relatedPartyTransactions', 'relatedParties'],
              newParties
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="关联方名称"
        />
      ),
    },
    {
      title: '关联关系',
      dataIndex: 'relationshipType',
      key: 'relationshipType',
      width: 150,
      render: (text: string, record: RelatedParty, index: number) => (
        <Select
          value={text}
          onChange={value => {
            const parties =
              form.getFieldValue([
                'relatedPartyTransactions',
                'relatedParties',
              ]) || [];
            const newParties = [...parties];
            newParties[index] = {
              ...newParties[index],
              relationshipType: value,
            };
            form.setFieldValue(
              ['relatedPartyTransactions', 'relatedParties'],
              newParties
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="关联关系"
          style={{ width: '100%' }}
        >
          <Option value="parent_company">母公司</Option>
          <Option value="subsidiary">子公司</Option>
          <Option value="associate">联营企业</Option>
          <Option value="joint_venture">合营企业</Option>
          <Option value="key_management">关键管理人员</Option>
          <Option value="major_shareholder">主要股东</Option>
          <Option value="other">其他</Option>
        </Select>
      ),
    },
    {
      title: '注册信息',
      dataIndex: 'registrationInfo',
      key: 'registrationInfo',
      width: 200,
      render: (text: string, record: RelatedParty, index: number) => (
        <TextArea
          value={text}
          onChange={e => {
            const parties =
              form.getFieldValue([
                'relatedPartyTransactions',
                'relatedParties',
              ]) || [];
            const newParties = [...parties];
            newParties[index] = {
              ...newParties[index],
              registrationInfo: e.target.value,
            };
            form.setFieldValue(
              ['relatedPartyTransactions', 'relatedParties'],
              newParties
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="注册地址、注册资本等"
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      ),
    },
    {
      title: '经营范围',
      dataIndex: 'businessScope',
      key: 'businessScope',
      width: 200,
      render: (text: string, record: RelatedParty, index: number) => (
        <TextArea
          value={text}
          onChange={e => {
            const parties =
              form.getFieldValue([
                'relatedPartyTransactions',
                'relatedParties',
              ]) || [];
            const newParties = [...parties];
            newParties[index] = {
              ...newParties[index],
              businessScope: e.target.value,
            };
            form.setFieldValue(
              ['relatedPartyTransactions', 'relatedParties'],
              newParties
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="主要经营范围"
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      ),
    },
    {
      title: '控制关系',
      dataIndex: 'controlRelationship',
      key: 'controlRelationship',
      width: 150,
      render: (text: string, record: RelatedParty, index: number) => (
        <TextArea
          value={text}
          onChange={e => {
            const parties =
              form.getFieldValue([
                'relatedPartyTransactions',
                'relatedParties',
              ]) || [];
            const newParties = [...parties];
            newParties[index] = {
              ...newParties[index],
              controlRelationship: e.target.value,
            };
            form.setFieldValue(
              ['relatedPartyTransactions', 'relatedParties'],
              newParties
            );
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="控制关系说明"
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (text: any, record: RelatedParty, index: number) => (
        <Button
          type="link"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => {
            const parties =
              form.getFieldValue([
                'relatedPartyTransactions',
                'relatedParties',
              ]) || [];
            const newParties = [...parties];
            newParties.splice(index, 1);
            form.setFieldValue(
              ['relatedPartyTransactions', 'relatedParties'],
              newParties
            );
            handleFormChange();
          }}
          disabled={readonly}
        >
          删除
        </Button>
      ),
    },
  ];

  return (
    <div>
      {/* 自动保存指示器 */}
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-xl font-semibold">财务尽职调查</h2>
        <AutoSaveIndicator
          saving={saving}
          saveStatus={saveStatus}
          lastSavedText={lastSavedText}
        />
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleFormChange}
        disabled={readonly}
      >
        {/* A. 财务报表完整性核查 */}
        <FormSection
          title="财务报表完整性核查"
          icon={<FileTextOutlined />}
          alert={{
            message: '请全面核查财务报表的完整性、准确性和合规性',
            type: 'info',
          }}
        >
          <FormSubSection title="报表完整性检查">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={[
                    'financialStatements',
                    'completenessCheck',
                    'hasBalanceSheet',
                  ]}
                  label="资产负债表"
                  rules={[DDValidationRules.required()]}
                >
                  <Radio.Group>
                    <Radio value={true}>已提供</Radio>
                    <Radio value={false}>未提供</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[
                    'financialStatements',
                    'completenessCheck',
                    'hasIncomeStatement',
                  ]}
                  label="利润表"
                  rules={[DDValidationRules.required()]}
                >
                  <Radio.Group>
                    <Radio value={true}>已提供</Radio>
                    <Radio value={false}>未提供</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[
                    'financialStatements',
                    'completenessCheck',
                    'hasCashFlowStatement',
                  ]}
                  label="现金流量表"
                  rules={[DDValidationRules.required()]}
                >
                  <Radio.Group>
                    <Radio value={true}>已提供</Radio>
                    <Radio value={false}>未提供</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={[
                    'financialStatements',
                    'completenessCheck',
                    'hasEquityStatement',
                  ]}
                  label="所有者权益变动表"
                  rules={[DDValidationRules.required()]}
                >
                  <Radio.Group>
                    <Radio value={true}>已提供</Radio>
                    <Radio value={false}>未提供</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[
                    'financialStatements',
                    'completenessCheck',
                    'hasNotes',
                  ]}
                  label="财务报表附注"
                  rules={[DDValidationRules.required()]}
                >
                  <Radio.Group>
                    <Radio value={true}>已提供</Radio>
                    <Radio value={false}>未提供</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
          </FormSubSection>

          <FormSubSection title="会计政策变更">
            <Form.Item
              name={['financialStatements', 'accountingPolicyChanges']}
              initialValue={[]}
            >
              <Form.Item noStyle shouldUpdate>
                {() => {
                  const changes =
                    form.getFieldValue([
                      'financialStatements',
                      'accountingPolicyChanges',
                    ]) || [];
                  return (
                    <>
                      <UnifiedDataTable
                        columns={accountingPolicyColumns}
                        dataSource={changes}
                        pagination={false}
                        size="small"
                        rowKey={(record, index) => `policy-change-${index}`}
                        locale={{
                          emptyText: '暂无会计政策变更记录，请点击下方按钮添加',
                        }}
                        scroll={{ x: 'max-content' }}
                      />
                      <Button
                        type="dashed"
                        onClick={() => {
                          const newChanges = [
                            ...changes,
                            {
                              changeDate: '',
                              policyArea: '',
                              oldPolicy: '',
                              newPolicy: '',
                              impact: '',
                              reason: '',
                            },
                          ];
                          form.setFieldValue(
                            ['financialStatements', 'accountingPolicyChanges'],
                            newChanges
                          );
                          handleFormChange();
                        }}
                        icon={<PlusOutlined />}
                        style={{ width: '100%', marginTop: 8 }}
                        disabled={readonly}
                      >
                        添加会计政策变更
                      </Button>
                    </>
                  );
                }}
              </Form.Item>
            </Form.Item>
          </FormSubSection>
        </FormSection>

        {/* B. 关联方交易核查 */}
        <FormSection
          title="关联方交易核查"
          icon={<ExclamationCircleOutlined />}
          alert={{
            message: '请全面核查关联方关系和交易的公允性',
            type: 'warning',
          }}
        >
          <FormSubSection title="关联方清单">
            <Form.Item
              name={['relatedPartyTransactions', 'relatedParties']}
              initialValue={[]}
            >
              <Form.Item noStyle shouldUpdate>
                {() => {
                  const parties =
                    form.getFieldValue([
                      'relatedPartyTransactions',
                      'relatedParties',
                    ]) || [];
                  return (
                    <>
                      <UnifiedDataTable
                        columns={relatedPartyColumns}
                        dataSource={parties}
                        pagination={false}
                        size="small"
                        rowKey={(record, index) => `related-party-${index}`}
                        locale={{
                          emptyText: '暂无关联方记录，请点击下方按钮添加',
                        }}
                        scroll={{ x: 'max-content' }}
                      />
                      <Button
                        type="dashed"
                        onClick={() => {
                          const newParties = [
                            ...parties,
                            {
                              partyName: '',
                              relationshipType: '',
                              registrationInfo: '',
                              businessScope: '',
                              controlRelationship: '',
                            },
                          ];
                          form.setFieldValue(
                            ['relatedPartyTransactions', 'relatedParties'],
                            newParties
                          );
                          handleFormChange();
                        }}
                        icon={<PlusOutlined />}
                        style={{ width: '100%', marginTop: 8 }}
                        disabled={readonly}
                      >
                        添加关联方
                      </Button>
                    </>
                  );
                }}
              </Form.Item>
            </Form.Item>
          </FormSubSection>
        </FormSection>

        {/* C. 或有负债分析 */}
        <FormSection
          title="或有负债分析"
          icon={<SafetyCertificateOutlined />}
          alert={{
            message: '请全面分析或有负债对公司财务状况的潜在影响',
            type: 'warning',
          }}
        >
          <FormSubSection title="担保情况">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={['contingentLiabilities', 'totalGuaranteeAmount']}
                  label="对外担保总额(万元)"
                  rules={[
                    DDValidationRules.required(),
                    DDValidationRules.amount(),
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    placeholder="对外担保总额"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={['contingentLiabilities', 'guaranteeRatio']}
                  label="担保比例(%)"
                  rules={[
                    DDValidationRules.required(),
                    DDValidationRules.percentage(),
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    precision={2}
                    placeholder="占净资产比例"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </FormSubSection>
        </FormSection>

        {/* D. 内控制度评估 */}
        <FormSection
          title="内控制度评估"
          icon={<SafetyCertificateOutlined />}
          alert={{
            message: '请评估内部控制制度的有效性和完整性',
            type: 'info',
          }}
        >
          <FormSubSection title="内控制度完整性">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={['internalControls', 'hasFinancialControl']}
                  label="财务内控制度"
                  rules={[DDValidationRules.required()]}
                >
                  <Radio.Group>
                    <Radio value={true}>健全</Radio>
                    <Radio value={false}>不健全</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={['internalControls', 'hasOperationalControl']}
                  label="运营内控制度"
                  rules={[DDValidationRules.required()]}
                >
                  <Radio.Group>
                    <Radio value={true}>健全</Radio>
                    <Radio value={false}>不健全</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={['internalControls', 'hasComplianceControl']}
                  label="合规内控制度"
                  rules={[DDValidationRules.required()]}
                >
                  <Radio.Group>
                    <Radio value={true}>健全</Radio>
                    <Radio value={false}>不健全</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
          </FormSubSection>
        </FormSection>

        {/* E. 财务风险评估 */}
        <FormSection
          title="财务风险评估"
          icon={<AlertOutlined />}
          alert={{
            message: '请基于财务尽职调查结果，全面评估财务风险',
            type: 'info',
          }}
        >
          <FormSubSection title="偿债能力分析">
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item
                  name={[
                    'financialRiskAssessment',
                    'solvencyAnalysis',
                    'currentRatio',
                  ]}
                  label="流动比率"
                  rules={[DDValidationRules.required()]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    placeholder="流动比率"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name={[
                    'financialRiskAssessment',
                    'solvencyAnalysis',
                    'quickRatio',
                  ]}
                  label="速动比率"
                  rules={[DDValidationRules.required()]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    placeholder="速动比率"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name={[
                    'financialRiskAssessment',
                    'solvencyAnalysis',
                    'debtToEquityRatio',
                  ]}
                  label="资产负债率(%)"
                  rules={[
                    DDValidationRules.required(),
                    DDValidationRules.percentage(),
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    precision={2}
                    placeholder="资产负债率"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name={[
                    'financialRiskAssessment',
                    'solvencyAnalysis',
                    'interestCoverageRatio',
                  ]}
                  label="利息保障倍数"
                  rules={[DDValidationRules.required()]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    placeholder="利息保障倍数"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name={[
                'financialRiskAssessment',
                'solvencyAnalysis',
                'assessmentConclusion',
              ]}
              label="偿债能力评估结论"
              rules={[
                DDValidationRules.required(),
                DDValidationRules.legalOpinion(50),
              ]}
            >
              <TextArea
                rows={4}
                placeholder="请基于上述指标分析公司的偿债能力（至少50字）"
              />
            </Form.Item>
          </FormSubSection>

          <FormSubSection title="财务风险矩阵">
            <Form.Item
              name={['financialRiskAssessment', 'riskMatrix']}
              initialValue={[]}
            >
              <RiskMatrix
                riskCategories={[
                  { value: 'liquidity_risk', label: '流动性风险' },
                  { value: 'credit_risk', label: '信用风险' },
                  { value: 'market_risk', label: '市场风险' },
                  { value: 'operational_risk', label: '经营风险' },
                  { value: 'financial_reporting_risk', label: '财务报告风险' },
                  { value: 'compliance_risk', label: '合规风险' },
                ]}
                readonly={readonly}
                emptyText="暂无财务风险评估，请点击下方按钮添加"
                addButtonText="添加财务风险"
                showResponsibleParty={true}
                showTimeline={true}
                onChange={value => {
                  form.setFieldValue(
                    ['financialRiskAssessment', 'riskMatrix'],
                    value
                  );
                  handleFormChange();
                }}
              />
            </Form.Item>
          </FormSubSection>

          <FormSubSection title="综合财务结论">
            <Form.Item
              name={[
                'financialRiskAssessment',
                'overallConclusion',
                'financialStability',
              ]}
              label="财务稳定性评估"
              rules={[DDValidationRules.required()]}
            >
              <Select placeholder="请选择财务稳定性评估结论">
                <Option value="stable">财务状况稳定</Option>
                <Option value="moderate_concerns">存在一般关注事项</Option>
                <Option value="significant_risks">存在重大财务风险</Option>
                <Option value="high_risk">财务风险较高</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name={[
                'financialRiskAssessment',
                'overallConclusion',
                'detailedAssessment',
              ]}
              label="详细财务评估"
              rules={[
                DDValidationRules.required(),
                DDValidationRules.legalOpinion(100),
              ]}
            >
              <TextArea
                rows={6}
                placeholder="请详细阐述财务尽职调查的结论和建议（至少100字）"
              />
            </Form.Item>

            <Form.Item
              name={[
                'financialRiskAssessment',
                'overallConclusion',
                'recommendations',
              ]}
              label="改进建议"
            >
              <TextArea rows={4} placeholder="请提出具体的财务管理改进建议" />
            </Form.Item>
          </FormSubSection>
        </FormSection>

        {/* 文档上传区域 */}
        <FormSection
          title="相关文档"
          icon={<FileTextOutlined />}
          alert={{
            message:
              '发行人和中介机构都可以上传相关文档。发行人提供基础材料，中介机构补充尽职调查报告和专业意见。',
            type: 'info',
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <DocumentUpload
                projectId={projectId}
                stepId="financial_due_diligence_statements"
                title="财务报表文件"
                description="审计报告、财务报表、附注等"
                maxFileSize={20}
                maxFileCount={15}
                allowedFileTypes={['.pdf', '.doc', '.docx', '.xls', '.xlsx']}
                readonly={readonly}
              />
            </Col>
            <Col span={12}>
              <DocumentUpload
                projectId={projectId}
                stepId="financial_due_diligence_analysis"
                title="财务分析文件"
                description="财务分析报告、比率分析、趋势分析等"
                maxFileSize={20}
                maxFileCount={10}
                allowedFileTypes={['.pdf', '.doc', '.docx', '.xls', '.xlsx']}
                readonly={readonly}
              />
            </Col>
          </Row>
        </FormSection>

        {/* 禁止在组件内部添加操作按钮！ */}
        {/* 所有操作必须通过统一底部操作栏处理 */}
      </Form>
    </div>
  );
};

export default FinancialDueDiligence;
